import React from "react";
import { Button } from "../ui/button";
import { Loader } from "../ui/loader";
import { useNavigate } from "react-router-dom";
import { CheckIcon } from "lucide-react";

/**
 * Loading state component
 */
export const LoadingState: React.FC<{ message?: string }> = ({ 
  message = "Loading Workflow..." 
}) => {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <Loader size="lg" variant="primary" className="mb-4" />
      <h2 className="text-xl font-semibold text-foreground mb-2">
        {message}
      </h2>
      <p className="text-muted-foreground">
        Please wait while we fetch the workflow details.
      </p>
    </div>
  );
};

/**
 * Error state component
 */
export const ErrorState: React.FC = () => {
  const navigate = useNavigate();
  
  return (
    <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
      <h2 className="text-xl font-semibold text-destructive mb-2">
        Error Loading Workflow
      </h2>
      <p className="text-destructive/80 mb-6">
        Failed to load workflow details. Please try again.
      </p>
      <Button
        onClick={() => navigate("/workflow")}
        variant="destructive"
        className="px-6"
      >
        Return to Workflow Selection
      </Button>
    </div>
  );
};

/**
 * Processing state component
 */
export const ProcessingState: React.FC = () => {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <Loader size="lg" variant="primary" className="mb-4" />
      <h2 className="text-xl font-semibold text-foreground mb-2">
        Processing...
      </h2>
      <p className="text-muted-foreground">
        Please wait while we analyze the data...
      </p>
    </div>
  );
};

/**
 * Migrating state component
 */
export const MigratingState: React.FC = () => {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <Loader size="lg" variant="primary" className="mb-4" />
      <h2 className="text-xl font-semibold text-foreground mb-2">
        Migrating...
      </h2>
      <p className="text-muted-foreground">
        Please wait while we process your request.
      </p>
    </div>
  );
};

/**
 * Success state component
 */
export const SuccessState: React.FC<{ onReset: () => void }> = ({ onReset }) => {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="rounded-full bg-primary/10 p-4 mb-4">
        <CheckIcon className="h-16 w-16 text-primary" />
      </div>
      <h2 className="text-xl font-semibold text-foreground mb-2">
        Migration Completed Successfully
      </h2>
      <p className="text-muted-foreground mb-8">
        All selected entities have been migrated.
      </p>

      <Button
        onClick={onReset}
        variant="default"
        className="px-6"
      >
        Start New Migration
      </Button>
    </div>
  );
};

/**
 * Combined WorkflowStates object for easier imports
 */
const WorkflowStates = {
  Loading: LoadingState,
  Error: ErrorState,
  Processing: ProcessingState,
  Migrating: MigratingState,
  Success: SuccessState,
};

export default WorkflowStates;
